import React from 'react';
import { useState } from 'react';

import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import * as Constants from '../../../components/api/constants';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

export default function OTAUploadFirmware(props) {
  const [file, setFile] = useState();
  const [filename, setFilename] = useState('No file selected');
  const [version, setVersion] = useState('');
  const parent = props;

  const handleFileUpload = (e) => {
    if (!e.target.files || !e.target.files[0]) return;

    setFile(e.target.files[0]);
    setFilename(e.target.files[0].name);
  };

  const uploadFirmware = () => {
    if (!file || !version) {
      toast.error('Please select a file and enter a version number.', {
        position: 'top-right',
        autoClose: 4000,
      });
      return;
    }

    const formData = new FormData();
    formData.append('Version', version);
    formData.append('Files', file);
    formData.append('DeviceType', parent.value.deviceType);

    const requestOptions = {
      method: 'POST',
      body: formData,
      credentials: 'include',
    };

    fetch(Constants.baseURL + Constants.apiUploadFirmware, requestOptions)
      .then((response) => {
        if (response.ok) {
          parent.reload();
          toast.success('Firmware uploaded successfully', {
            position: 'top-right',
            autoClose: 5000,
          });
        } else {
          toast.error('Firmware upload failed. Please try again', {
            position: 'top-right',
            autoClose: 5000,
          });
        }
      })
      .catch((error) => {
        console.log('request failed', error);
        toast.error('Firmware upload failed. Please try again', {
          position: 'top-right',
          autoClose: 5000,
        });
      });
  };

  const canUpload = !!file && !!version;

  return (
    <>
      <ToastContainer />

      {/* Align header with card */}
      <Box sx={{ width: '100%', mb: 2 }}>
        <Typography
          component="h2"
          variant="h6"
          sx={{ color: 'white', fontWeight: 600 }}
        >
          Upload New Firmware
        </Typography>
      </Box>

      {/* Full-width card */}
      <Paper
        sx={{
          p: 3,
          width: '100%',
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Grid container spacing={3}>
          {/* File Input */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}
            >
              Firmware file
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'stretch', sm: 'center' },
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                component="label"
                startIcon={<UploadFileIcon />}
                sx={{
                  textTransform: 'none',
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.08)',
                  },
                }}
              >
                Choose file
                <input
                  type="file"
                  accept=".bin,.hex"
                  hidden
                  onChange={handleFileUpload}
                />
              </Button>

              <Chip
                icon={<DescriptionOutlinedIcon />}
                label={filename}
                sx={{
                  flexGrow: 1,
                  maxWidth: '100%',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  borderRadius: 2,
                  color: 'rgba(255,255,255,0.9)',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  },
                }}
              />
            </Box>
          </Grid>

          {/* Version Input */}
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}
            >
              Version
            </Typography>

            <TextField
              fullWidth
              variant="outlined"
              placeholder="e.g. 1.0.3"
              value={version}
              onChange={(event) => setVersion(event.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(0,0,0,0.3)',
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255,255,255,0.25)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255,255,255,0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#90CAF9',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255,255,255,0.5)',
                },
              }}
            />
          </Grid>

          {/* Upload button */}
          <Grid item xs={12}>
            <Button
              variant="contained"
              fullWidth
              disabled={!canUpload}
              onClick={uploadFirmware}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                py: 1.1,
                bgcolor: canUpload ? 'primary.main' : 'rgba(255,255,255,0.18)',
                color: canUpload ? 'white' : 'rgba(255,255,255,0.5)',
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: canUpload ? 'primary.dark' : 'rgba(255,255,255,0.2)',
                  boxShadow: 'none',
                },
              }}
            >
              Upload Firmware
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
